import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { useAuth } from "./AuthContext";

const notificationContext = createContext(null);

export const useNavNotifications = () => useContext(notificationContext);

const getTimeBucket = (ts) => {
  const d = new Date(ts);
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
};

const getStableKey = (row, userId, compId) => {
  const bucket = getTimeBucket(row.ist_datetime) || row.activity_date || "";
  return JSON.stringify([compId, userId, row.client || "", row.message || "", bucket]);
};

export const NotificationProvider = ({ children, API }) => {
  const { companyData = [], selectedCompany, user } = useAuth();

  const userId = user?.UserId;

  const [notifications, setNotifications] = useState([]);
  const [newNotifications, setNewNotifications] = useState([]);
  const [notifLoading, setNotifLoading] = useState(false);
  const [browserNotifEnabled, setBrowserNotifEnabled] = useState(false);

  const unreadCount = notifications.filter(n => !n.read).length;
  const shownKeysRef = useRef(new Set());
  const isInitialized = useRef(false);

  const toastStorageKey = (compId, userId) =>
    `toastShownKeys_${compId}_${userId}`;

  const readStorageKey = (compId, userId) =>
    `readNotifications_${compId}_${userId}`;

  const cleanupTimestampKey = (compId, userId) =>
    `toastCleanup_${compId}_${userId}`;

  /* ----------------------------------------------------------- */
  /* STORAGE WITH TIMESTAMPS                                     */
  /* ----------------------------------------------------------- */

  const saveShownKey = (compId, userId, stableKey) => {
    try {
      const saved = localStorage.getItem(toastStorageKey(compId, userId));
      const shownData = saved ? JSON.parse(saved) : {};

      shownData[stableKey] = Date.now();

      localStorage.setItem(toastStorageKey(compId, userId), JSON.stringify(shownData));
      shownKeysRef.current.add(stableKey);
    } catch (err) {
      console.error("Error saving shown key:", err);
    }
  };

  const cleanupOldShownKeys = (compId, userId) => {
    const CLEANUP_DAYS = 7;
    const CLEANUP_INTERVAL = 24 * 60 * 60 * 1000;

    try {
      const lastCleanup = localStorage.getItem(cleanupTimestampKey(compId, userId));
      const now = Date.now();

      if (lastCleanup && now - parseInt(lastCleanup) < CLEANUP_INTERVAL) {
        return;
      }

      const saved = localStorage.getItem(toastStorageKey(compId, userId));
      if (!saved) return;

      const shownData = JSON.parse(saved);
      const cutoffTime = now - (CLEANUP_DAYS * 24 * 60 * 60 * 1000);

      const recentData = {};
      let removedCount = 0;

      for (const [key, timestamp] of Object.entries(shownData)) {
        if (timestamp > cutoffTime) {
          recentData[key] = timestamp;
        } else {
          removedCount++;
        }
      }

      localStorage.setItem(toastStorageKey(compId, userId), JSON.stringify(recentData));
      localStorage.setItem(cleanupTimestampKey(compId, userId), now.toString());

      if (removedCount > 0) {
        // old keys pruned
      }
    } catch (err) {
      console.error("Error cleaning up old keys:", err);
    }
  };

  const initializeShownKeys = (compId, userId) => {
    if (!compId || !userId) return;

    try {
      cleanupOldShownKeys(compId, userId);

      const saved = localStorage.getItem(toastStorageKey(compId, userId));
      if (saved) {
        const shownData = JSON.parse(saved);
        shownKeysRef.current = new Set(Object.keys(shownData));
      } else {
        shownKeysRef.current = new Set();
      }
    } catch (err) {
      console.error("Error loading shown keys:", err);
      shownKeysRef.current = new Set();
    }
  };



  /* ----------------------------------------------------------- */
  /* READ / UNREAD PERSISTENCE                                   */
  /* ----------------------------------------------------------- */

  const getReadIds = (compId, userId) => {
    try {
      const raw = localStorage.getItem(readStorageKey(compId, userId));
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  };

  const addReadId = (compId, userId, id) => {
    const existing = getReadIds(compId, userId);
    localStorage.setItem(
      readStorageKey(compId, userId),
      JSON.stringify([...new Set([...existing, id])])
    );
  };

  const addReadIdsBulk = (compId, userId, ids) => {
    const existing = getReadIds(compId, userId);
    localStorage.setItem(
      readStorageKey(compId, userId),
      JSON.stringify([...new Set([...existing, ...ids])])
    );
  };

  /* ----------------------------------------------------------- */
  /* TIMESTAMP-BASED "NEWNESS" CHECK                             */
  /* ----------------------------------------------------------- */

  const isRecentNotification = (row) => {
    try {
      const timestamp = row.ist_datetime || row.activity_date || row.created_at;

      if (!timestamp) return false;

      const notifDate = new Date(timestamp);
      const now = new Date();
      const hoursDiff = (now - notifDate) / (1000 * 60 * 60);

      return hoursDiff < 24;
    } catch (err) {
      console.error("Error parsing notification date:", err);
      return false;
    }
  };

  /* ----------------------------------------------------------- */
  /* BROWSER NOTIFICATION FUNCTIONS                              */
  /* ----------------------------------------------------------- */

  const requestNotificationPermission = useCallback(async () => {
    if (!("Notification" in window)) {
      console.log("Browser doesn't support notifications");
      return false;
    }

    if (Notification.permission === "granted") {
      return true;
    }

    if (Notification.permission !== "denied") {
      const permission = await Notification.requestPermission();
      return permission === "granted";
    }

    return false;
  }, []);

  const showBrowserNotification = (notif) => {
    if (Notification.permission === "granted") {
      const title = notif.client || "New Notification";
      const body = `${notif.message || "Activity"} - Doc: ${notif.documentNumber || "N/A"}`;

      const notification = new Notification(title, {
        body: body,
        icon: "/logo.png",
        badge: "/badge.png",
        tag: notif.id,
        requireInteraction: false,
        silent: false,
      });

      notification.onclick = () => {
        window.focus();
        handleNotificationClick(notif.id);
        notification.close();
      };

      setTimeout(() => notification.close(), 5000);
    }
  };

  /* ----------------------------------------------------------- */
  /* FETCH NOTIFICATIONS FROM API                                */
  /* ----------------------------------------------------------- */

  const fetchNotifications = async (compId) => {
    if (!compId || !userId) return;

    setNotifLoading(true);

    try {
      const res = await axios.get(`${API}/api/recent-activity`, {
        params: {
          CompId: compId,
          userId: userId
        }
      });

      const data = res?.data?.data || [];
      const unseen = [];
      const storedReadIds = getReadIds(compId, userId);

      const mapped = data.map((row, idx) => {
        const stableKey = getStableKey(row, userId, compId);
        const id = stableKey || `api-${idx}`;
        const alreadyShown = shownKeysRef.current.has(stableKey);
        const isRecent = isRecentNotification(row);

        const showLeadInfo =
         row.activity_type  === "Inquiry Follow-up" ||
          row.activity_type === "New Inquiry";

        const notif = {
          id,
          message: row.message || row.activity_type || "Activity",
          documentNumber : row.document_number ,
          client: row.client || "",
          time: row.time_ago || "",
          raw: row,
          remarks: row.remarks,
          leadFrom: showLeadInfo ? row.leadFrom : null,
          leadTo: showLeadInfo ? row.leadTo : null,
          read: storedReadIds.includes(id),
          _key: stableKey
        };

        if (
          stableKey &&
          !shownKeysRef.current.has(stableKey) &&
          isRecentNotification(row)
        ) {
          unseen.push(notif);
          saveShownKey(compId, userId, stableKey);

          showBrowserNotification(notif);
        }

        return notif;
      });

      if (unseen.length > 0) {
        setNewNotifications((prev) => [...unseen, ...prev]);
      }

      setNotifications(mapped);
    } catch (err) {
      console.error("Notifications fetch error:", err);
    } finally {
      setNotifLoading(false);
    }
  };

  /* ----------------------------------------------------------- */
  /* REQUEST PERMISSION ON MOUNT                                 */
  /* ----------------------------------------------------------- */

  useEffect(() => {
    requestNotificationPermission().then(enabled => {
      setBrowserNotifEnabled(enabled);
    });
  }, []);

  /* ----------------------------------------------------------- */
  /* READ ACTIONS                                                */
  /* ----------------------------------------------------------- */

  const handleNotificationClick = useCallback((id) => {
    const compId = selectedCompany?.CompId || companyData?.[0]?.CompId;

    if (!compId || !userId) return;

    setNotifications(prev =>
      prev.map(n => n.id === id ? { ...n, read: true } : n)
    );

    addReadId(compId, userId, id);
    console.log('✓ Marked notification as read:', id);
  }, [selectedCompany, companyData, userId]);

  const handleMarkAllRead = useCallback(() => {
    const compId = selectedCompany?.CompId || companyData?.[0]?.CompId;

    if (!compId || !userId) return;

    const ids = notifications.map(n => n.id);
    addReadIdsBulk(compId, userId, ids);

    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
  }, [notifications, selectedCompany, companyData, userId]);

  /* ----------------------------------------------------------- */
  /* INITIALIZE ON COMPANY CHANGE                                */
  /* ----------------------------------------------------------- */

  useEffect(() => {
    const compId = selectedCompany?.CompId || companyData?.[0]?.CompId;

    if (!compId || !userId) return;

    initializeShownKeys(compId, userId);
    isInitialized.current = true;

  }, [selectedCompany?.CompId, companyData?.[0]?.CompId, userId]);

  /* ----------------------------------------------------------- */
  /* FETCH + POLLING                                             */
  /* ----------------------------------------------------------- */

  useEffect(() => {
    const compId = selectedCompany?.CompId || companyData?.[0]?.CompId;

    if (!compId || !userId || !isInitialized.current) return;

    fetchNotifications(compId);

    const interval = setInterval(() => {
      fetchNotifications(compId);
    }, 60000);

    return () => clearInterval(interval);
  }, [selectedCompany?.CompId, companyData?.[0]?.CompId, API, userId]);

  /* ----------------------------------------------------------- */
  /* CLEAR TOAST AFTER CLOSE                                     */
  /* ----------------------------------------------------------- */



  const clearNewNotifications = useCallback(() => {
    setNewNotifications([]);
  }, []);

  const clearAllStoredData = useCallback(() => {
    const compId = selectedCompany?.CompId || companyData?.[0]?.CompId;
    if (!compId || !userId) return;

    localStorage.removeItem(toastStorageKey(compId, userId));
    localStorage.removeItem(readStorageKey(compId, userId));
    localStorage.removeItem(cleanupTimestampKey(compId, userId));
    shownKeysRef.current.clear();
  }, [selectedCompany, companyData, userId]);

  /* ----------------------------------------------------------- */
  /* CONTEXT VALUE                                               */
  /* ----------------------------------------------------------- */

  const contextValue = useMemo(() => ({
    notifications,
    newNotifications,
    notifLoading,
    unreadCount,
    handleNotificationClick,
    handleMarkAllRead,
    clearNewNotifications,
    clearAllStoredData,
    browserNotifEnabled,
    requestNotificationPermission
  }), [
    notifications,
    newNotifications,
    notifLoading,
    unreadCount,
    handleNotificationClick,
    handleMarkAllRead,
    clearNewNotifications,
    clearAllStoredData,
    browserNotifEnabled,
    requestNotificationPermission
  ]);

  return (
    <notificationContext.Provider
      value={contextValue}
    >
      {children}
    </notificationContext.Provider>
  );
};

